/*
 * <zpen-scroll-pen>
 * Scroll-driven ZPEN animation: while the section scrolls past, the paper strip in the window turns
 * (one line at a time) and at the end the cap twists shut and shows the ZPEN logo.
 *
 * The pen photo is a fixed 2000 x 2000 stage. The cap is drawn on a canvas as a real cylinder (unrolled
 * texture projected for the current rotation), so the geometry below is measured on zpen-scroll-pen-bg.jpg.
 * If you replace that photo, the numbers in GEOMETRY have to be measured again.
 */
(() => {
  if (customElements.get('zpen-scroll-pen')) return;

  // ---- GEOMETRY (rectified photo space, pen axis horizontal) ----
  const CW = 1455, CH = 313; // cap canvas size
  const R = 158.5, CYR = 412.5; // cylinder radius and axis row
  const Y0 = 256; // photo row of the first canvas row
  const D2R = Math.PI / 180;
  const PSI0 = -60, TPD = 4; // texture covers psi -60..300 deg, 4 rows per degree
  const HOLE = { a: -46.4, b: 20.6, x0: 197, x1: 1357 }; // window opening
  const DRUM = { a: -41.4, b: 18.2, x: 217, w: 1123, h: 154 }; // paper drum seen through the window
  const LOGO = { psi: 180, r: 201, cy: 471, srcY: 340, w: 473, x: 539, srcW: 600 };
  const WHEEL_R = 97, WHEEL_STEP = 0.62; // paper strip: wheel radius, angle between lines
  const WHEEL_END = 0.66, TWIST_START = 0.64, TWIST_LEN = 0.34; // scroll progress milestones
  const PROFILE = [69, 67, 66, 63, 61, 60, 59, 58, 56, 55, 54, 53, 52, 51, 50, 49, 49, 48, 47, 46, 45, 45, 44, 43, 42, 41, 41, 40, 39, 39, 38, 37, 36, 35, 35, 35, 35, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 33, 33, 34, 33, 33, 33, 33, 34, 34, 34, 34, 34, 33, 33, 33, 33, 33, 34, 34, 34, 34, 34, 34, 35, 35, 36, 36, 36, 36, 36, 37, 37, 37, 38, 39, 39, 39, 39, 39, 39, 39, 39, 39, 39, 40, 40, 40, 39, 39, 39, 38, 38, 38, 38, 38, 37, 37, 36, 36, 36, 36, 35, 35, 34, 34, 33, 33, 33, 32, 32, 31, 31, 30, 30, 29, 29, 28, 28, 27, 26, 24, 22, 20, 18, 16, 15, 15, 15, 15, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 13, 14, 14, 14, 14, 13, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 15, 15, 16, 16, 17, 18, 20, 22, 23, 24, 25, 26, 27, 27, 28, 28, 29, 29, 29, 30, 30, 31, 31, 31, 31, 31, 32, 31, 31, 32, 32, 33, 32, 33, 33, 33, 33, 33, 33, 33, 33, 33, 33, 33, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 34, 35, 35, 35, 35, 35, 35, 35, 35, 36, 36, 36, 36, 36, 37, 37, 37, 37, 37, 38, 38, 38, 39, 39, 40, 40, 40, 40, 41, 41, 42, 42, 43, 43, 44, 44, 45, 45, 46, 47, 47, 48, 50, 52, 53, 55, 57, 61, 63, 62, 63, 72, 84, 83]; // static gloss of the black cap, one value per canvas row

  const clamp01 = (x) => Math.max(0, Math.min(1, x));
  const mod = (a, m) => ((a % m) + m) % m;
  const rowOf = (psi) => (psi - PSI0) * TPD;
  const makeCanvas = (w, h) => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  };

  let lightCanvas = null;
  function getLight() {
    if (lightCanvas) return lightCanvas;
    lightCanvas = makeCanvas(CW, CH);
    const l = lightCanvas.getContext('2d');
    const id = l.createImageData(CW, CH);
    for (let y = 0; y < CH; y++) {
      const v = Math.max(0, PROFILE[y] - 8);
      for (let x = 0; x < CW; x++) {
        const i = (y * CW + x) * 4;
        id.data[i] = v;
        id.data[i + 1] = v;
        id.data[i + 2] = v + 1;
        id.data[i + 3] = 255;
      }
    }
    l.putImageData(id, 0, 0);
    return lightCanvas;
  }

  class ZpenScrollPen extends HTMLElement {
    connectedCallback() {
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
    }

    setup() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('.zsp__stage');
      this.box = this.querySelector('.zsp__box');
      this.cap = this.querySelector('.zsp__cap');
      this.hint = this.querySelector('.zsp__hint');
      if (!this.stage || !this.box || !this.cap) return;

      this.inline = this.dataset.mode === 'inline';
      this.lineEls = Array.from(this.querySelectorAll('[data-zsp-line]'));
      this.lines = this.lineEls.map((el) => el.textContent.trim());
      if (!this.lines.length) this.lines = ['ZPEN'];
      this.n = this.lines.length;

      this.ctx = this.cap.getContext('2d');
      this.tex = makeCanvas(CW, 360 * TPD); // window + paper drum
      this.texB = makeCanvas(CW, 360 * TPD); // plain cap + logo
      this.layerA = makeCanvas(CW, CH);
      this.layerB = makeCanvas(CW, CH);
      this.strip = makeCanvas(DRUM.w, DRUM.h);
      this.tctx = this.tex.getContext('2d');
      this.bctx = this.texB.getContext('2d');
      this.sctx = this.strip.getContext('2d');

      this.logo = new Image();
      this.logoDrawn = false;
      this.logo.onload = () => {
        this.rev++;
      };
      this.logo.src = this.dataset.logo;

      this.pos = 0;
      this.target = 0;
      this.twist = 0;
      this.twistT = 0;
      this.rev = 0;
      this.lastKey = '';
      this.forceLine = null;
      this.visible = false;
      this.raf = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.fontFamily = (getComputedStyle(this).fontFamily || 'Arial') + ', Arial, sans-serif';

      this.onScroll = () => this.readScroll();
      this.onFrame = () => this.frame();
      this.onBlockSelect = (event) => {
        const index = this.lineEls.indexOf(event.target.closest('[data-zsp-line]'));
        if (index < 0) return;
        this.forceLine = index;
        this.readScroll();
        this.start();
      };
      this.onBlockDeselect = () => {
        this.forceLine = null;
        this.readScroll();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this.box);
      this.fit();

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          if (this.visible) this.start();
        },
        { rootMargin: '100px' }
      );
      this.intersectionObserver.observe(this);

      // attention flash around the frame every time it scrolls into view (at least half of it visible)
      if (!this.reduced) {
        this.flashTarget = this.closest('.zw__media') || this.box;
        this.flashObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            if (!entry.isIntersecting) return;
            const el = this.flashTarget;
            el.classList.remove('zsp-flash');
            void el.offsetWidth; // restart the animation
            el.classList.add('zsp-flash');
          },
          { threshold: 0.5 }
        );
        this.flashObserver.observe(this.box);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.rev++;
          this.start();
        });
      }

      if (this.reduced) {
        // still frame instead of a scroll scene
        this.classList.add('zsp--static');
        this.target = this.pos = Math.min(2, this.n - 1);
        this.hint?.classList.add('is-hidden');
      } else {
        this.readScroll();
      }
    }

    teardown() {
      if (!this.ready) return;
      this.ready = false;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.flashObserver?.disconnect();
    }

    fit() {
      // cover the box with the square stage, centred (the inline box can be 4:3 on phones)
      const w = this.box.clientWidth;
      const h = this.box.clientHeight || w;
      const scale = Math.max(w, h) / 2000;
      const x = (w - 2000 * scale) / 2;
      const y = (h - 2000 * scale) / 2;
      this.stage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }

    start() {
      if (!this.raf) this.raf = requestAnimationFrame(this.onFrame);
    }

    // scroll position of the section -> wheel position and cap twist
    readScroll() {
      if (this.reduced) return;
      if (this.forceLine !== null) {
        // theme editor: preview the selected line, cap open
        this.target = this.forceLine;
        this.twistT = 0;
        return;
      }
      if (this.inline) {
        // plays while the box travels from the bottom of the screen to the top
        const box = this.box.getBoundingClientRect();
        const vh = window.innerHeight;
        const q = clamp01((vh * 0.95 - box.top) / (vh * 0.9));
        this.target = clamp01(q / WHEEL_END) * (this.n - 1);
        this.twistT = clamp01((q - TWIST_START) / TWIST_LEN);
        return;
      }
      const rect = this.getBoundingClientRect();
      const sticky = this.querySelector('.zsp__sticky');
      const stickyTop = sticky ? parseFloat(getComputedStyle(sticky).top) || 0 : 0;
      const total = rect.height - (sticky ? sticky.offsetHeight : window.innerHeight);
      const p = total > 0 ? clamp01((stickyTop - rect.top) / total) : 0;
      this.target = clamp01(p / WHEEL_END) * (this.n - 1);
      this.twistT = clamp01((p - TWIST_START) / TWIST_LEN);
      if (this.hint) this.hint.classList.toggle('is-hidden', p > 0.02);
    }

    frame() {
      this.raf = 0;
      if (!this.ready || (!this.visible && this.forceLine === null)) return;

      this.pos += (this.target - this.pos) * 0.22;
      if (Math.abs(this.target - this.pos) < 0.001) this.pos = this.target;
      this.twist += (this.twistT - this.twist) * 0.05;
      if (Math.abs(this.twistT - this.twist) < 0.0005) this.twist = this.twistT;

      const e = this.twist * this.twist * (3 - 2 * this.twist);
      const posKey = this.pos.toFixed(3) + '|' + this.rev;
      const key = posKey + '|' + e.toFixed(4);
      if (key !== this.lastKey) {
        const textChanged = posKey !== this.lastKey.split('|').slice(0, 2).join('|');
        this.lastKey = key;
        if (textChanged || !this.drawn) {
          this.drawTexture(this.pos);
          this.drawn = true;
        }
        this.render(e, textChanged || this.layerADirty !== false);
        this.layerADirty = false;
      }
      this.start();
    }

    // ---- paper strip (the wheel) ----
    drawStrip(pos) {
      const c = this.sctx;
      const W = DRUM.w, H = DRUM.h, cy = H / 2;
      c.clearRect(0, 0, W, H);
      const g = c.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#cfcdc9');
      g.addColorStop(0.5, '#f6f5f2');
      g.addColorStop(1, '#c6c4c0');
      c.fillStyle = g;
      c.fillRect(0, 0, W, H);
      for (let k = -3; k <= 3; k++) {
        const idx = Math.round(pos) + k;
        const a = (idx - pos) * WHEEL_STEP;
        if (Math.abs(a) > 1.3) continue;
        c.save();
        c.translate(46, cy + WHEEL_R * Math.sin(a));
        c.scale(1, Math.cos(a));
        c.font = `500 44px ${this.fontFamily}`;
        c.textBaseline = 'middle';
        c.fillStyle = '#16161a';
        c.fillText(this.lines[mod(idx, this.n)], 0, 0, W - 80);
        c.restore();
      }
      const gl = c.createLinearGradient(0, 0, 0, H);
      gl.addColorStop(0, 'rgba(0,0,0,.35)');
      gl.addColorStop(0.22, 'rgba(0,0,0,0)');
      gl.addColorStop(0.4, 'rgba(255,255,255,.18)');
      gl.addColorStop(0.55, 'rgba(255,255,255,0)');
      gl.addColorStop(1, 'rgba(0,0,0,.30)');
      c.fillStyle = gl;
      c.fillRect(0, 0, W, H);
    }

    // ---- unrolled cap surface: black body + window opening + paper drum ----
    drawTexture(pos) {
      const t = this.tctx;
      this.drawStrip(pos);
      t.fillStyle = '#080809';
      t.fillRect(0, 0, this.tex.width, this.tex.height);
      t.fillStyle = '#050506';
      t.fillRect(HOLE.x0, rowOf(HOLE.a), HOLE.x1 - HOLE.x0, rowOf(HOLE.b) - rowOf(HOLE.a));
      // paper drum, stretched over the cylinder angle so it looks right at any rotation
      const sT = Math.sin(DRUM.a * D2R);
      const sB = Math.sin(DRUM.b * D2R);
      for (let r = Math.floor(rowOf(DRUM.a)); r < rowOf(DRUM.b); r++) {
        const p0 = r / TPD + PSI0;
        const p1 = (r + 1) / TPD + PSI0;
        const s0 = ((Math.sin(p0 * D2R) - sT) / (sB - sT)) * DRUM.h;
        const s1 = ((Math.sin(p1 * D2R) - sT) / (sB - sT)) * DRUM.h;
        if (s1 < 0 || s0 > DRUM.h) continue;
        const sy = Math.max(0, s0);
        t.drawImage(this.strip, 0, sy, DRUM.w, Math.max(0.5, Math.min(DRUM.h, s1) - sy), DRUM.x, r, DRUM.w, 1);
      }
    }

    // plain black cap with the logo on the opposite side (drawn once, after the image has loaded)
    drawLogoTexture() {
      if (this.logoDrawn || !(this.logo.complete && this.logo.naturalWidth)) return;
      this.logoDrawn = true;
      const c = this.bctx;
      c.fillStyle = '#080809';
      c.fillRect(0, 0, this.texB.width, this.texB.height);
      const scale = LOGO.w / LOGO.srcW;
      for (let r = Math.floor(rowOf(LOGO.psi - 40)); r < rowOf(LOGO.psi + 25); r++) {
        const f0 = (r / TPD + PSI0 - LOGO.psi) * D2R;
        const f1 = ((r + 1) / TPD + PSI0 - LOGO.psi) * D2R;
        const y0 = LOGO.cy + LOGO.r * Math.sin(f0) - LOGO.srcY;
        const y1 = LOGO.cy + LOGO.r * Math.sin(f1) - LOGO.srcY;
        if (y1 < 0 || y0 > this.logo.naturalHeight) continue;
        const sy = Math.max(0, y0);
        const sh = Math.max(0.5, Math.min(this.logo.naturalHeight, y1) - sy);
        c.drawImage(this.logo, 0, sy, this.logo.naturalWidth, sh, LOGO.x, r, this.logo.naturalWidth * scale, 1);
      }
    }

    // project the cylinder: draw the texture rotated by `delta` degrees
    renderTo(c, texture, delta) {
      c.clearRect(0, 0, CW, CH);
      for (let y = 0; y < CH; y++) {
        const yy = Y0 + y - CYR;
        const a0 = Math.asin(Math.max(-1, Math.min(1, yy / R))) / D2R;
        const a1 = Math.asin(Math.max(-1, Math.min(1, (yy + 1) / R))) / D2R;
        let p0 = a0 + delta;
        let p1 = a1 + delta;
        const shift = Math.floor((p0 - PSI0) / 360) * 360;
        p0 -= shift;
        p1 -= shift;
        if (p1 > PSI0 + 360 || p1 < p0) p1 = p0 + 1 / TPD;
        const r0 = Math.min(rowOf(p0), texture.height - 1);
        c.drawImage(texture, 0, r0, CW, Math.max(1, Math.min(texture.height - r0, (p1 - p0) * TPD)), 0, y, CW, 1);
      }
    }

    // The window never rotates. The black, twisting cap slides up over it from below and covers it.
    // `e` is the eased twist progress 0..1.
    render(e, dirtyA) {
      const ctx = this.ctx;
      if (dirtyA) this.renderTo(this.layerA.getContext('2d'), this.tex, 0);
      ctx.clearRect(0, 0, CW, CH);
      ctx.drawImage(this.layerA, 0, 0);
      if (e > 0) {
        this.drawLogoTexture();
        this.renderTo(this.layerB.getContext('2d'), this.texB, 180 * e);
        const s = clamp01(e / 0.75); // the cover is fully across at 75 % of the twist
        const sm = s * s * (3 - 2 * s);
        const yb = (CH + 24) * (1 - sm) - 12; // cover edge (canvas row), moves bottom -> top
        const edge = ctx.createLinearGradient(0, yb - 22, 0, yb);
        edge.addColorStop(0, 'rgba(0,0,0,0)');
        edge.addColorStop(1, 'rgba(0,0,0,.55)');
        ctx.fillStyle = edge;
        ctx.fillRect(0, yb - 22, CW, 22);
        const top = Math.max(0, yb);
        ctx.drawImage(this.layerB, 0, top, CW, CH - top, 0, top, CW, CH - top);
        const rim = ctx.createLinearGradient(0, yb, 0, yb + 5);
        rim.addColorStop(0, 'rgba(255,255,255,.16)');
        rim.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = rim;
        ctx.fillRect(0, yb, CW, 5);
      }
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(getLight(), 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  customElements.define('zpen-scroll-pen', ZpenScrollPen);
})();
